import type { Session } from '@/contexts/AuthContext'
import { USER_ROLE_KEYS } from '@/constants/enums'
import type { LocalDevLoginResponse } from './types'

const TOKEN_KEY = 'pams.localDev.token'
const SESSION_KEY = 'pams.localDev.session'
const SESSION_VERSION_KEY = 'pams.sessionVersion'

const VALID_ROLE_KEYS: ReadonlySet<string> = new Set<string>(USER_ROLE_KEYS as readonly string[])

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

/**
 * session_version は対になる認証トークンと同じ保存スコープに置く。
 * - local-dev トークンは localStorage（タブ間で共有）
 * - Cognito トークンは sessionStorage（タブ単位。{@link ../../config/amplify.ts} 参照）
 * スコープがずれると、同一ブラウザの別タブで別アカウントにログインした際に
 * このキーを奪い合い、「自分の JWT × 他タブの版数」が混在して誤って 401
 * （別のデバイスでログイン）になる。トークンと同スコープに揃えて衝突を防ぐ。
 */
function sessionVersionStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  return isLocalDevLoginEnabled() ? window.localStorage : window.sessionStorage
}

export function isLocalDevLoginEnabled(): boolean {
  return import.meta.env.VITE_LOCAL_DEV_LOGIN === 'true'
}

export function getLocalDevToken(): string | null {
  if (!isLocalDevLoginEnabled() || !hasWindow()) return null
  return window.localStorage.getItem(TOKEN_KEY)
}

export function getLocalDevSession(): Session | null {
  if (!isLocalDevLoginEnabled() || !hasWindow()) return null

  const raw = window.localStorage.getItem(SESSION_KEY)
  if (!raw) return null

  let session: Session
  try {
    session = JSON.parse(raw) as Session
  } catch {
    clearLocalDevAuth()
    return null
  }

  // 新スキーマ前提: role は UserRole enum key、roleDisplay と permissions は必須。
  // 旧 session（日本語 role や permissions 欠落）が残っていたら破棄して再ログインへ。
  if (
    !session?.role
    || !VALID_ROLE_KEYS.has(session.role)
    || typeof session.roleDisplay !== 'string'
    || !Array.isArray(session.permissions)
  ) {
    clearLocalDevAuth()
    return null
  }

  return session
}

export function saveLocalDevAuth(payload: LocalDevLoginResponse): Session {
  const session: Session = {
    cognitoSub: payload.cognitoSub,
    municipality: payload.municipality || '',
    municipalityId: payload.municipalityId ?? null,
    municipalitySubdomain: payload.municipalitySubdomain ?? null,
    ocrUse: payload.ocrUse ?? false,
    loginId: payload.loginId,
    displayName: payload.displayName,
    role: payload.role,
    roleDisplay: payload.roleDisplay ?? '',
    permissions: payload.permissions ?? [],
    sessionVersion: payload.sessionVersion,
  }

  if (hasWindow()) {
    window.localStorage.setItem(TOKEN_KEY, payload.token)
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    if (payload.sessionVersion != null) {
      saveSessionVersion(payload.sessionVersion)
    }
  }

  return session
}

export function clearLocalDevAuth(): void {
  if (!hasWindow()) return
  window.localStorage.removeItem(TOKEN_KEY)
  window.localStorage.removeItem(SESSION_KEY)
  // 版数は環境により localStorage / sessionStorage どちらにも入りうるため両方から消す。
  window.localStorage.removeItem(SESSION_VERSION_KEY)
  if (typeof window.sessionStorage !== 'undefined') {
    window.sessionStorage.removeItem(SESSION_VERSION_KEY)
  }
}

export function getSessionVersion(): number | null {
  const storage = sessionVersionStorage()
  if (!storage) return null
  const raw = storage.getItem(SESSION_VERSION_KEY)
  if (!raw) return null
  const val = parseInt(raw, 10)
  return Number.isNaN(val) ? null : val
}

export function saveSessionVersion(version: number): void {
  const storage = sessionVersionStorage()
  if (!storage) return
  storage.setItem(SESSION_VERSION_KEY, String(version))
}
